import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { GET_AGENT_FOLLOWUP_LIST } from "../../constants";
import axios from "axios";
import dayjs from "dayjs";
import { set } from "date-fns";

// Helper function to format date
const formatDate = (dateString) => {
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// Colors for charts
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

// Dashboard Component
const AgentsToDoListComponent = () => {
  const [data, setData] = useState({
    summary: {
      totalLeads: 0,
      totalBdms: 0,
      totalActions: 0,
      completedActions: 0,
      pendingActions: 0,
      completionRate: 0,
    },
    bdms: [],
    agentInfo: { agentName: "", agentId: "" },
    dateRange: { startDate: "", endDate: "" },
  });
  const [selectedBdmIndex, setSelectedBdmIndex] = useState(0);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(1, "day").format("YYYY-MM-DD"), // Last 7 days
    endDate: dayjs().format("YYYY-MM-DD"), // Today
  });

  const [startDate, setStartDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD") // Last 7 days
  );
  const [endDate, setEndDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD") // Yesterday
  );

  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const dateRangeOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last7days" },
    { label: "Last 30 Days", value: "last30days" },
    { label: "This Month", value: "thismonth" },
    { label: "Last Month", value: "lastmonth" },
    { label: "Custom", value: "custom" },
  ];

  const [selectedDateRange, setSelectedDateRange] = useState("yesterday");

  const handleDateRangeChange = (value) => {
    setSelectedDateRange(value);
    let newStartDate, newEndDate;

    switch (value) {
      case "today":
        newStartDate = dayjs().format("YYYY-MM-DD");
        newEndDate = dayjs().format("YYYY-MM-DD");
        break;
      case "yesterday":
        newStartDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
        newEndDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
        break;
      case "last7days":
        newStartDate = dayjs().subtract(7, "day").format("YYYY-MM-DD");
        newEndDate = dayjs().format("YYYY-MM-DD");
        break;
      case "last30days":
        newStartDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
        newEndDate = dayjs().format("YYYY-MM-DD");
        break;
      case "thismonth":
        newStartDate = dayjs().startOf("month").format("YYYY-MM-DD");
        newEndDate = dayjs().format("YYYY-MM-DD");
        break;
      case "lastmonth":
        newStartDate = dayjs()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        newEndDate = dayjs()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD");
        break;
      case "custom":
        setDateFilterOpen(true);
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        return; // Don't fetch data yet for custom range
      default:
        return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
    fetchFollowUpData(newStartDate, newEndDate);
  };

  const applyCustomDateRange = () => {
    if (dayjs(tempEndDate).isBefore(dayjs(tempStartDate))) {
      alert("End date must be after start date");
      return;
    }

    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setDateFilterOpen(false);
    fetchFollowUpData(tempStartDate, tempEndDate);
  };

  const fetchFollowUpData = async (selectedStartDate, selectedEndDate) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${GET_AGENT_FOLLOWUP_LIST}?agentId=${localStorage.getItem(
          "username"
        )}&startDate=${selectedStartDate}&endDate=${selectedEndDate}`
      );

      if (
        response.data?.dateRange?.startDate &&
        response.data?.dateRange?.endDate
      ) {
        setDateRange({
          startDate: dayjs(response.data.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(response.data.endDate).format("YYYY-MM-DD"),
        });
      }

      setData(response.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data on component mount
    fetchFollowUpData(startDate, endDate);
  }, []);

  // Create data for completion rate pie chart with null checks
  const completionRateData = [
    { name: "Completed", value: data?.summary?.completedActions || 0 },
    { name: "Pending", value: data?.summary?.pendingActions || 0 },
  ];

  // Create data for BDM performance bar chart with null checks
  const bdmPerformanceData =
    data?.bdms?.map((bdm) => ({
      name: bdm?.bdmName || "Unknown",
      completed: bdm?.stats?.completedActions || 0,
      pending: bdm?.stats?.pendingActions || 0,
      completion_rate:
        bdm?.stats?.totalActions > 0
          ? Math.round(
              ((bdm?.stats?.completedActions || 0) / bdm?.stats?.totalActions) *
                100
            )
          : 0,
    })) || [];

  // Get the selected BDM with null check
  const selectedBdm =
    data?.bdms && data.bdms.length > 0
      ? data.bdms[selectedBdmIndex >= data.bdms.length ? 0 : selectedBdmIndex]
      : { stats: {}, leads: [] };

  console.log(selectedBdm, "selectedBdm");

  // Create data for action types distribution for the selected BDM with null checks
  const actionTypesData = [
    { name: "Meetings", value: selectedBdm?.stats?.meetingActions || 0 },
    { name: "RO Visits", value: selectedBdm?.stats?.roVisitActions || 0 },
    { name: "HO Visits", value: selectedBdm?.stats?.hoVisitActions || 0 },
    { name: "BO Visits", value: selectedBdm?.stats?.boVisitActions || 0 },
    { name: "Travel", value: selectedBdm?.stats?.travelActions || 0 },
    { name: "Site Visits", value: selectedBdm?.stats?.siteVisitActions || 0 },
  ].filter((item) => item.value > 0);

  // Handle lead click to show details
  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetails(true);
  };

  // Handle back button click in lead details view
  const handleBackClick = () => {
    setShowLeadDetails(false);
    setSelectedLead(null);
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dayjs(dateString).format("MMM D, YYYY");
  };

  // Define chart colors
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884d8",
    "#82ca9d",
  ];

  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const statusFilters = ["Completed", "Not Completed", "No Action Taken"];

  function getLeadStatus(lead) {
    if (lead.action_status) return "No Action Taken";
    if (
      lead.actions?.some((action) => action.completion_status === "completed")
    )
      return "Completed";
    return "Not Completed";
  }

  const filteredLeads = selectedBdm.leads.filter((lead) => {
    if (!selectedStatusFilter) return true; // no filter, show all
    return getLeadStatus(lead) === selectedStatusFilter;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Loading dashboard...</p>
          {/* You can add a spinner here */}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                BDM Follow-up Dashboard
              </h1>
              <p className="text-gray-600">
                CSE: {data?.agentInfo?.agentName || "Loading..."}
                {data?.agentInfo?.agentId ? ` (${data.agentInfo.agentId})` : ""}
              </p>
            </div>

            {/* Date Range Filter */}
            <div className="relative">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="w-full sm:w-48">
                  <label className="block text-xs text-gray-500 mb-1">
                    Date Range
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedDateRange}
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                  >
                    {dateRangeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="self-end">
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                    onClick={() => fetchFollowUpData(startDate, endDate)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div> */}
              </div>

              <div className="mt-2 text-sm font-medium text-gray-800">
                {formatDate(startDate)} - {formatDate(endDate)}
              </div>

              {/* Custom Date Range Popup */}
              {dateFilterOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg z-10 p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-800 mb-3">
                    Custom Date Range
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        value={tempStartDate}
                        onChange={(e) => setTempStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        value={tempEndDate}
                        onChange={(e) => setTempEndDate(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        onClick={() => setDateFilterOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        onClick={applyCustomDateRange}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm">Total Leads</h3>
            <p className="text-3xl font-bold text-gray-800">
              {data?.summary?.totalLeads || 0}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm">Total BDMs</h3>
            <p className="text-3xl font-bold text-gray-800">
              {data?.summary?.totalBdms || 0}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm">Total Actions</h3>
            <p className="text-3xl font-bold text-gray-800">
              {data?.summary?.totalActions || 0}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm">Completion Rate</h3>
            <p className="text-3xl font-bold text-gray-800">
              {data?.summary?.completionRate || 0}%
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Completion Rate Pie Chart */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Overall Completion Rate
            </h2>
            <div className="h-64">
              {completionRateData.some((item) => item.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={completionRateData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {completionRateData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full justify-center items-center text-gray-500">
                  No data available
                </div>
              )}
            </div>
          </div>

          {/* BDM Performance Bar Chart */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              BDM Performance
            </h2>
            <div className="h-64">
              {bdmPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={bdmPerformanceData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name"  />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" name="Completed" fill="#00C49F" />
                    <Bar dataKey="pending" name="Pending" fill="#FF8042" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full justify-center items-center text-gray-500">
                  No BDM data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BDM Selection and Details */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              BDM Details
            </h2>
            <div className="flex flex-wrap gap-2">
              {data?.bdms?.length > 0 ? (
                data.bdms.map((bdm, index) => (
                  <button
                    key={bdm?.bdmId || index}
                    className={`px-3 py-1 rounded-full text-sm ${
                      selectedBdmIndex === index
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                    onClick={() => {
                      setSelectedBdmIndex(index);
                      setShowLeadDetails(false);
                    }}
                  >
                    {bdm?.bdmName || `BDM ${index + 1}`}
                  </button>
                ))
              ) : (
                <p className="text-gray-500">No BDMs available</p>
              )}
            </div>
          </div>

          {/* Selected BDM Stats - Only show if bdms exist */}
          {data?.bdms?.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded p-3">
                  <h3 className="text-gray-500 text-sm">Lead Count</h3>
                  <p className="text-xl font-bold text-gray-800">
                    {selectedBdm?.stats?.totalLeads || 0}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <h3 className="text-gray-500 text-sm">Action Count</h3>
                  <p className="text-xl font-bold text-gray-800">
                    {selectedBdm?.stats?.totalActions || 0}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <h3 className="text-gray-500 text-sm">Completion Rate</h3>
                  <p className="text-xl font-bold text-gray-800">
                    {selectedBdm?.stats?.totalActions > 0
                      ? Math.round(
                          ((selectedBdm?.stats?.completedActions || 0) /
                            selectedBdm?.stats?.totalActions) *
                            100
                        )
                      : 0}
                    %
                  </p>
                </div>
              </div>

              {/* Action Types Pie Chart */}
              {/* <div className="mb-6">
                <h3 className="text-md font-semibold text-gray-700 mb-2">Action Type Distribution</h3>
                <div className="h-64">
                  {actionTypesData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={actionTypesData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {actionTypesData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full justify-center items-center text-gray-500">
                      No action type data available
                    </div>
                  )}
                </div>
              </div> */}

              {/* Lead List or Lead Details */}
              {showLeadDetails && selectedLead ? (
                <div>
                  <div className="flex items-center mb-4">
                    <button
                      onClick={handleBackClick}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 19l-7-7m0 0l7-7m-7 7h18"
                        />
                      </svg>
                      Back to Lead List
                    </button>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {selectedLead?.leadInfo?.CustomerName || "No Name"}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-600">
                          <span className="font-medium">Mobile:</span>{" "}
                          {selectedLead?.leadInfo?.MobileNo || "N/A"}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">Location:</span>{" "}
                          {selectedLead?.leadInfo?.location || "N/A"}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">Category:</span>{" "}
                          {selectedLead?.leadInfo?.category || "N/A"} -{" "}
                          {selectedLead?.leadInfo?.sub_category || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">
                          <span className="font-medium">Agent Remark:</span>{" "}
                          {selectedLead?.leadInfo?.agent_remark || "No remarks"}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">BDM Remark:</span>{" "}
                          {selectedLead?.leadInfo?.bdm_remark || "No remarks"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <h4 className="text-md font-semibold text-gray-700 mb-2">
                    Actions
                  </h4>
                  <div className="space-y-3">
                    {selectedLead?.actions?.length > 0 ? (
                      selectedLead.actions.map((action) => (
                        <div
                          key={action?.id || Math.random()}
                          className="border rounded-lg p-3"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span
                                className={`inline-block px-2 py-1 text-xs rounded ${
                                  action?.completion_status === "completed"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {action?.completion_status === "completed"
                                  ? "Completed"
                                  : "Not Completed"}
                              </span>
                              <span className="inline-block ml-2 px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                                {action?.action_type || "No Action Type"}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {action?.action_date || "No date"}
                            </div>
                          </div>

                          <h5 className="font-medium text-gray-800">
                            {action?.specific_action || "No specific action"}
                          </h5>

                          {action?.remarks && (
                            <p className="text-gray-600 text-sm mt-1">{selectedLead?.leadInfo?.bdm_remark}</p>
                          )}

                          {/* Travel Form Details */}
                          {action?.travel_form_details && (
                            <div className="mt-3 pt-2 border-t border-gray-100">
                              <h6 className="text-sm font-medium text-gray-700">
                                Travel Details
                              </h6>
                              <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                <p className="text-gray-600">
                                  <span className="font-medium">
                                    Task Type:
                                  </span>{" "}
                                  {action.travel_form_details?.taskType ||
                                    "N/A"}
                                </p>
                                {action.travel_form_details
                                  ?.purposeForVisit && (
                                  <p className="text-gray-600">
                                    <span className="font-medium">
                                      Purpose:
                                    </span>{" "}
                                    {action.travel_form_details.purposeForVisit.replace(
                                      "_",
                                      " "
                                    )}
                                  </p>
                                )}
                                {action.travel_form_details
                                  ?.concernPersonName && (
                                  <p className="text-gray-600">
                                    <span className="font-medium">Person:</span>{" "}
                                    {
                                      action.travel_form_details
                                        .concernPersonName
                                    }
                                  </p>
                                )}
                                {action.travel_form_details?.modeOfTravel && (
                                  <p className="text-gray-600">
                                    <span className="font-medium">Mode:</span>{" "}
                                    {action.travel_form_details.modeOfTravel}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">
                        No actions available for this lead
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-md font-semibold text-gray-700 mb-2">
                    Lead List
                  </h3>
                  {selectedBdm?.leads?.length > 0 ? (
                    <>
                      <div className="mb-4 flex space-x-2">
                        {statusFilters.map((status) => (
                          <button
                            key={status}
                            onClick={() => setSelectedStatusFilter(status)}
                            className={`px-3 py-1 rounded-full text-sm font-semibold cursor-pointer
        ${
          selectedStatusFilter === status
            ? "bg-blue-600 text-white"
            : "bg-gray-200 text-gray-700"
        }`}
                          >
                            {status}
                          </button>
                        ))}
                        <button
                          onClick={() => setSelectedStatusFilter(null)}
                          className={`px-3 py-1 rounded-full text-sm font-semibold cursor-pointer
      ${
        selectedStatusFilter === null
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-gray-700"
      }`}
                        >
                          All
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Category
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {filteredLeads.map((lead) => (
                              <tr key={lead?.leadInfo?.id || Math.random()}>
                                <td className="py-2 px-3 whitespace-nowrap">
                                  <div className="font-medium text-gray-800">
                                    {lead?.leadInfo?.CustomerName || "No Name"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {lead?.leadInfo?.MobileNo || "N/A"}
                                  </div>
                                </td>
                                <td className="py-2 px-3 whitespace-nowrap text-sm text-gray-500">
                                  {lead?.leadInfo?.location || "N/A"}
                                </td>
                                <td className="py-2 px-3 whitespace-nowrap text-sm text-gray-500">
                                  {lead?.leadInfo?.category || "N/A"} -{" "}
                                  {lead?.leadInfo?.sub_category || "N/A"}
                                </td>
                                <td className="py-2 px-3 whitespace-nowrap text-sm text-gray-500">
                                  {lead?.actions?.length || 0}
                                </td>
                                {lead?.action_status ? (
                                  <td className="py-2 px-3 whitespace-nowrap">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-400 ">
                                      No Action Taken
                                    </span>
                                  </td>
                                ) : (
                                  <td className="py-2 px-3 whitespace-nowrap">
                                    {lead?.actions?.some(
                                      (action) =>
                                        action?.completion_status ===
                                        "completed"
                                    ) ? (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        Completed
                                      </span>
                                    ) : (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                        Not Completed
                                      </span>
                                    )}
                                  </td>
                                )}
                                <td className="py-2 px-3 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleLeadClick(lead)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500">
                      No leads available for this BDM
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">
              No BDM data available. Please wait for data to load or try
              refreshing.
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgentsToDoListComponent;
